<template>
  <main role="main" class="py-4">
    <b-container fluid="xxl" class="mb-3">
      <div class="d-flex flex-row align-items-center justify-content-between mb-4">
        <h4 class="m-0">{{ $t("serviceAdminAccountList") }}</h4> 
        <div>
          <b-button variant="outline-primary" v-b-modal:new-account-modal>
            <b-icon icon="plus"></b-icon> {{ $t("createNew") }}
          </b-button>
        </div>
      </div>

      <div class="d-flex flex-row align-items-center justify-content-between">
        <b-form inline class="mb-4" @submit.prevent="onSubmitSearch">
          <div class="mr-3">{{ $t("search") }}:</div>
          <b-form-input type="search" v-model="searchText" minlength="2" maxlength="50"
                        style="min-width: 400px;"
                        :placeholder="$t('adminSearchPlaceholder')" class="mr-3" />
          <b-button type="submit" variant="primary">{{ $t("search") }}</b-button>
        </b-form>
        <div v-if="adminAccounts && adminAccounts.length">{{ $t("resultCount", { count: adminAccounts.length }) }}</div>
      </div>

      <b-table :items="adminAccounts" hover primary-key="id" :fields="tableFields" :busy="isLoading" show-empty>
        <template v-slot:table-busy>
          <div class="text-center text-secondary py-4">{{ $t("loadingData") }}</div>
        </template>
        <template v-slot:empty>
          <div class="text-center text-secondary py-4">{{ $t("noData") }}</div>
        </template>
        <template v-slot:cell(name)="data">
          {{ data.item.name }}
        </template>
        <template v-slot:cell(actions)="data">
          <b-dropdown v-if="isUserSuperAdmin()" size="lg" variant="link" toggle-class="text-decoration-none p-0" no-caret right menu-class="shadow">
            <template v-slot:button-content>
              <b-icon icon="three-dots"/>
            </template>
            <b-dropdown-item-button v-if="canEdit(data.item)" @click="onClickEditAdminUser(data.item)">{{ $t("updateInfo") }}</b-dropdown-item-button>
            <b-dropdown-item-button v-if="canEdit(data.item)" @click="onClickResetPassword(data.item)">{{ $t("resetPassword") }}</b-dropdown-item-button>
            <b-dropdown-item-button v-if="canDelete(data.item)" @click="onClickDeleteAdminUser(data.item)">{{ $t("actionDelete") }}</b-dropdown-item-button>
          </b-dropdown>
        </template>
      </b-table>
    </b-container>

    <b-modal id="new-account-modal" :title="$t('createServiceAdminAccount')"
             dialog-class="min-width-700" hide-footer no-close-on-backdrop>
      <NewAdminAccountModal @submitted="fetchAdmins" />
    </b-modal>

    <b-modal id="edit-account-modal" :title="$t('updateAdminAccount')" hide-footer no-close-on-backdrop>
      <EditAdminAccountModal :editing-user="userToEdit" @submitted="fetchAdmins" />
    </b-modal>

    <b-modal id="reset-password-modal" :title="$t('resetAdminAccountPassword')"
             hide-footer no-close-on-backdrop>
      <ResetPasswordModal :target-user="userToEdit" @submitted="fetchAdmins" />
    </b-modal>

  </main>
</template>

<style lang="scss" scoped>

</style>

<script lang="ts">
  import {Component, Vue} from 'vue-property-decorator';
  import _ from "lodash";
  import BaseComponent from "@/components/BaseComponent";
  import {BvTableFieldArray} from "bootstrap-vue";

  import * as util from "@/service/util";
  import * as userService from "@/service/userService";
  import EditAdminAccountModal from "@/components/admin-accounts/EditAdminAccountModal.vue";
  import NewAdminAccountModal from "@/components/admin-accounts/NewAdminAccountModal.vue";
  import ResetPasswordModal from "@/components/account/ResetPasswordModal.vue";
  import { SearchRequestManager } from "@/utils/search-request-manager";


  @Component({
    components: { NewAdminAccountModal, EditAdminAccountModal, ResetPasswordModal }
  })
  export default class AdminAccounts extends BaseComponent {

    searchText = "";
    searchManager = new SearchRequestManager();

    isLoading = false;
    adminAccounts = [];
    tableFields: BvTableFieldArray = [
      {
        key: "id",
        label: "#"
      },
      {
        key: "name",
        label: this.$t("fullName").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "username",
        label: "Username",
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "phone",
        label: this.$t("phoneNumber").toString(),
      },
      {
        key: "email",
        label: "Email",
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "createdBy",
        label: this.$t("creator").toString(),
        thClass: "text-nowrap",
        tdClass: "text-break",
      },
      {
        key: "createdAt",
        label: this.$t("createdDate").toString(),
        /*formatter: value => moment(value).format("DD/MM/YYYY")*/
      },
      {
        key: "actions",
        label: ""
      }
    ];

    userToEdit = null;


    mounted() {
      this.fetchAdmins();
    }

    onClickReload() {
      this.searchText = "";
      this.fetchAdmins();
    }
    debouncedfetchAdmins = _.debounce(() => {
      this.fetchAdmins();
    }, 300);

    onSubmitSearch() {
      this.searchManager.onSubmit({
        debounceFn: this.debouncedfetchAdmins,
        execute: () => { this.fetchAdmins(); }
      });
    }

    async fetchAdmins() {
      const currentRequestId = this.searchManager.nextId();
      try {
        this.isLoading = true;
        this.adminAccounts = null;
        const res = await userService.getAdmins({
          q: this.searchText?.trim()
        });
        if (!this.searchManager.isLatest(currentRequestId)) {
          return;
        }
        this.adminAccounts = res;
      } catch (e) {
        if (this.searchManager.isLatest(currentRequestId)) {
          this.toastError(e);
        }
      } finally {
        if (this.searchManager.isLatest(currentRequestId)) {
          this.isLoading = false;
        }
      }
    }


    onClickEditAdminUser(user) {
      this.userToEdit = user;
      this.$bvModal.show("edit-account-modal");

    }

    onClickResetPassword(user) {
      this.userToEdit = user;
      this.$bvModal.show("reset-password-modal");
    }

    canEdit(user): boolean {
      return this.isUserSuperAdmin() && this.userId !== user.id;
    }

    canDelete(user): boolean {
      return this.isUserSuperAdmin() && this.userId !== user.id;
    }


    async onClickDeleteAdminUser(user) {
      const modalMessage = this.$t("deleteAccountConfirmation", { account: user.name }).toString() + `(ID: ${user.id})?`;
      const confirmDelete = await this.$bvModal.msgBoxConfirm(modalMessage, {
        okVariant: 'danger',
        okTitle: this.$t("yes").toString(),
        cancelTitle: this.$t("no").toString(),
      });

      if (confirmDelete) {
        try {
          this.isLoading = true;
          await userService.deleteUser(user.id);
          this.onClickReload();
          this.toastSuccessDelay(this.$t("deleteAccountSuccess").toString());

        } catch (e) {
          this.isLoading = false;
          this.toastError(e);
        }
      }
    }

  }
</script>
